import React, { useRef } from 'react';

type InputRadioProps = {
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
  checked?: boolean;
  description?: string;
  disabled?: boolean;
  id?: string;
  label?: string | React.ReactNode;
  name?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  value?: string;
};

// Stable client-side id generator (React 17 has no useId).
let inputRadioInstanceId = 0;

function InputRadio({
  checked = false,
  description = '',
  disabled = false,
  label = '',
  name,
  onChange = () => {},
  value,
  ...props
}: InputRadioProps) {
  const instanceIdRef = useRef<string>();
  if (instanceIdRef.current === undefined) {
    inputRadioInstanceId += 1;
    instanceIdRef.current = `input-radio-${inputRadioInstanceId}`;
  }

  const descriptionId = description ? `${instanceIdRef.current}-description` : undefined;
  // Merge the internal description id with any aria-describedby injected by a
  // parent (e.g. RadioGroup) so neither overwrites the other.
  const describedBy = [props['aria-describedby'], descriptionId].filter(Boolean).join(' ') || undefined;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onChange(e, e.target.checked);
  }

  return (
    <label className={`input-radio${disabled ? ' input-radio--disabled' : ''}`}>
      <input
        checked={checked}
        className="input-radio__input"
        disabled={disabled}
        name={name}
        onChange={handleChange}
        type="radio"
        value={value}
        {...props}
        aria-describedby={describedBy}
      />
      <span className="input-radio__circle" aria-hidden="true" />
      <div className="input-radio__texts">
        <span className="input-radio__label">{label}</span>
        {description && (
          <span aria-hidden="true" className="input-radio__description" id={descriptionId}>
            {description}
          </span>
        )}
      </div>
    </label>
  );
}

export default InputRadio;
